import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { LogeEvent, LogeMember } from "../../types/entities";
import { EmberGlow } from "../dss/EmberGlow";
import { Icon } from "../dss/Icon";
import { LiveDot } from "../dss/LiveDot";

dayjs.locale(da);

// The same fine grain the countdown card on the calendar uses, as a data URI
// so it costs no request. At this opacity it reads as the tooth of a printed
// page rather than as noise.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="font-mono text-[9px] tracking-[1.6px] uppercase text-white/40">{children}</div>
);

/**
 * How far off the samling is, in the words you'd actually use for it.
 * Counted in whole calendar days rather than elapsed hours: at eleven at
 * night, something happening the next morning is under 24 hours away and is
 * still "i morgen", not "i dag".
 */
const distanceLabel = (date: string): string => {
  const days = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
  if (days < 0) return "Afholdt";
  if (days === 0) return "I dag";
  if (days === 1) return "I morgen";
  return `Om ${days} dage`;
};

/**
 * The masthead of a samling: one dark panel carrying the title, when it is,
 * and who is hosting — everything you came to the page to read, before any
 * of the controls.
 *
 * Full-bleed and unpadded at the sides on purpose. The rest of the page sits
 * inside the usual gutter, so the hero reads as the poster the page is
 * pinned under rather than as the first of a stack of cards. Nothing in it
 * changes when attendance does, which is also what makes it a stable ceiling
 * for everything below.
 */
export function EventHero({
  event,
  host,
  votingOpen,
  onBack,
}: {
  event: LogeEvent;
  host?: LogeMember;
  votingOpen: boolean;
  /** Back to the calendar — rendered inside the hero itself, since the hero
   * now owns the very top of the page. */
  onBack: () => void;
}) {
  const confirmed = event.status === "confirmed";
  const cancelled = event.status === "cancelled";

  const status = cancelled
    ? { text: "Aflyst", className: "bg-clay-soft/20 text-clay-soft", live: false }
    : confirmed
      ? { text: distanceLabel(event.date), className: "bg-white/10 text-white/75", live: false }
      : votingOpen
        ? { text: "Afstemning åben", className: "bg-vote-yes-soft/15 text-vote-yes-soft", live: true }
        : { text: "Afventer dato", className: "bg-white/10 text-white/60", live: false };

  return (
    // pt-safe here, not on the page around it: the hero is the first thing
    // on the page, so it's the one that has to paint its own way up under
    // the notch rather than leave a strip of paper above it.
    <div className="relative overflow-hidden bg-ink text-paper rounded-b-[22px] pt-safe">
      {/* Ember light gathering in the top-left corner, the same warm cast the
          countdown card has — it keeps the flat ink panel from reading as a
          black rectangle. Lightly alive, same as the countdown card's: see
          EmberGlow for why it's two independent layers, not one pulse. */}
      <EmberGlow
        ember="radial-gradient(230% 170% at 8% 4%, rgba(166,126,68,0.30) 0%, rgba(166,126,68,0.09) 40%, transparent 70%)"
        emberOrigin="8% 4%"
        corner="radial-gradient(190% 150% at 100% 100%, rgba(26,72,36,0.36) 0%, transparent 62%)"
        borderRadius="rounded-b-[22px]"
      />
      {/* The emoji as a watermark bleeding off the corner rather than a badge
          pinned in the margin — at this size it's texture, not an icon. */}
      <div
        aria-hidden
        className="absolute -right-7 -bottom-10 text-[150px] leading-none opacity-[0.09] select-none pointer-events-none"
      >
        {event.emoji}
      </div>
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.055] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: GRAIN }}
      />

      <div className="relative px-7 pt-4 pb-7">
        <button
          onClick={onBack}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-white/55 flex items-center gap-1.5 mb-5"
        >
          <Icon name="chevron-left" size={12} />
          <span>Kalender</span>
        </button>

        <div className="flex items-start justify-between gap-3">
          <div className="font-mono text-[9px] tracking-[1.8px] uppercase text-white/45 pt-1.5">
            Samling
          </div>
          <div
            className={`flex items-center gap-2 rounded-full px-2.5 py-1.5 font-mono text-[9px] tracking-[1.3px] uppercase ${status.className}`}
          >
            {status.live && <LiveDot color="bg-vote-yes-soft" size={6} />}
            <span>{status.text}</span>
          </div>
        </div>

        <h1 className="font-display text-[40px] leading-[0.9] tracking-tight font-extrabold mt-4 uppercase break-words">
          {event.title}
        </h1>

        <div className="h-px bg-white/15 mt-6" />

        <div className="mt-4 flex items-start gap-5">
          <div className="min-w-0 flex-1">
            <Label>[ Dato ]</Label>
            {cancelled ? (
              // The chip above already says Aflyst; repeating the word here
              // would leave the page saying it twice and the date — the one
              // thing a reader still wants from a cancelled samling — not at
              // all. Struck through instead, and only if there was ever a
              // settled date to strike.
              <div className="font-display text-[15px] font-bold uppercase tracking-tight text-white/40 mt-1.5 line-through decoration-clay-soft/70">
                {event.is_confirmed
                  ? dayjs(event.date).format("D. MMMM YYYY")
                  : "Ikke fastlagt"}
              </div>
            ) : confirmed ? (
              <>
                {/* Date only, never a time of day: the hour isn't settled
                    until roughly a week out, so printing one here would be a
                    guess presented as fact. */}
                <div className="font-display text-[15px] font-bold uppercase tracking-tight mt-1.5">
                  {dayjs(event.date).format("D. MMMM YYYY")}
                </div>
                <div className="font-mono text-[9.5px] tracking-[1.2px] uppercase text-white/40 mt-1">
                  {dayjs(event.date).format("dddd")}
                </div>
              </>
            ) : (
              <div className="font-display text-[15px] font-bold uppercase tracking-tight text-white/50 mt-1.5">
                Ikke fastlagt endnu
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <Label>[ Vært ]</Label>
            {host ? (
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[15px] leading-none">{host.emoji}</span>
                <span className="font-display text-[15px] font-bold uppercase tracking-tight truncate">
                  {host.name}
                </span>
              </div>
            ) : (
              <div className="font-display text-[15px] font-bold uppercase tracking-tight text-white/50 mt-1.5">
                Ingen vært
              </div>
            )}
          </div>
        </div>

        {!cancelled && (
          <div className="flex items-center gap-2 mt-5 font-mono text-[9px] tracking-[1.2px] uppercase text-white/40">
            <Icon name="clock" variant="regular" size={10} />
            <span>Klokkeslæt meldes ud cirka en uge før</span>
          </div>
        )}
      </div>
    </div>
  );
}
